import crossIcon from "src/assets/cross.svg";
import { useToast } from "src/hooks/useToast";
import { useEffect, useState } from "react";
import { Autocomplete, Button, Input } from "src/components";
import { escalateInvoiceToMaster, getMasterEscalationList, getRemarkHeader, getTextractData } from "src/services/invoice-api"; //M10-15136
import { queryCounterDetailsById } from "src/services/counter-api"; //M10-15136
import { useSelector } from "react-redux";
import { RootState } from "src/context/store";

//M10-15136
// Remark ids (from /stockist_attachment/escalation-reason) that should only be offered when the
// counter master is missing the respective value.
const GST_NOT_PRESENT_REMARK_ID = 1;
const DL_NOT_PRESENT_REMARK_ID = 2;
const MCI_NOT_PRESENT_REMARK_ID = 3;
// MCI applies only to DRDS-linked counters; DL applies only to these pharmacy/doctor link types.
const MCI_LINK_TYPES = ["DRDS"];
const DL_LINK_TYPES = ["DRAPH", "INDPH-PH", "INDPH-HS"];
//M10-15136

const EscalateToMaster = ({ onCloseModal, onSuccess }: { onCloseModal: () => void; onSuccess: () => void }) => {
    const { toast } = useToast();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { invoiceDetails } = useSelector((state: RootState) => state.invoice);
    // const { withEventCheck } = useEventStatus();
    const [remarks, setRemarks] = useState<string>("");
    const [remarkType, setRemarkType] = useState<any>("");
    const [remarkshrd, setRemarkHdr] = useState<any>([]);
    // Whether the counter master (fetched via the Counter Code API) has these values on file.
    const [counterHasGstNumber, setCounterHasGstNumber] = useState<boolean>(true); //M10-15136
    const [counterHasMciNumber, setCounterHasMciNumber] = useState<boolean>(true); //M10-15136
    const [counterHasDlNumber, setCounterHasDlNumber] = useState<boolean>(true); //M10-15136
    const invoiceHasGstNumber = !!invoiceDetails?.counter_gst_number?.toString()?.trim(); //M10-15136
    const [invoiceHasMciNumber, setInvoiceHasMciNumber] = useState<boolean>(false); //M10-15136
    const [invoiceHasDlNumber, setInvoiceHasDlNumber] = useState<boolean>(false); //M10-15136

    const entryType = invoiceDetails?.is_textracted ? "textract" : "manual";
    //M10-15136
    const linkType = invoiceDetails?.link_type;
    const isMciApplicableLinkType = MCI_LINK_TYPES.includes(linkType);
    const isDlApplicableLinkType = DL_LINK_TYPES.includes(linkType);

    // Each "<X> not present in master" remark is only offered when the counter master is missing
    // the value AND the invoice actually carries one — the point of the escalation is to push the
    // invoice's value into the master, so when neither side has it there is nothing to push. The
    // two values are never compared against each other; only their presence matters.
    const remarkOptions = remarkshrd?.filter((item: any) => {
        if (item?.id === GST_NOT_PRESENT_REMARK_ID) return !counterHasGstNumber && invoiceHasGstNumber;
        if (item?.id === MCI_NOT_PRESENT_REMARK_ID) return isMciApplicableLinkType && !counterHasMciNumber && invoiceHasMciNumber;
        if (item?.id === DL_NOT_PRESENT_REMARK_ID) return isDlApplicableLinkType && !counterHasDlNumber && invoiceHasDlNumber;
        return true;
    });
    //M10-15136

    const handleSaveChanges = async () => {
        if (remarkType == 5 && !remarks.trim()) {
            toast({ description: "Please enter remarks", variant: "destructive" });
            return;
        }
        //M10-15136
        try {
            const existingRes = await getMasterEscalationList({
                fuid: invoiceDetails?.fuid_no,
                remark_type: remarkType,
                company_code: invoiceDetails?.company_code,
            });
            const alreadyEscalated = (existingRes?.data?.data || []).some(
                (item: any) => item?.status === "pending" || item?.status === "on hold"
            );
            if (alreadyEscalated) {
                toast({ description: "This FUID is already escalated with this remark type", variant: "destructive" });
                return;
            }
        } catch (error) {
            console.log(error);
        }
        //M10-15136
        const payload = {
            "stockist_id": invoiceDetails?.stockist_id || "",
            "counter_code": invoiceDetails?.counter_code || "",
            "entry_type": entryType,
            "link_type": invoiceDetails?.link_type || "",
            "pharmacist_code": invoiceDetails?.pharmacy_code || "",
            "fuid": invoiceDetails?.fuid_no || "",
            "fuid_path": invoiceDetails?.file_path || "",
            "remark_type": remarkType,
            "comments": remarks,
            "invoice_number": invoiceDetails?.invoice?.invoice_number || ""
        }
        try {
            setIsLoading(true);
            const response = await escalateInvoiceToMaster(payload, invoiceDetails?.company_code);
            // await withEventCheck(
            //     () => {
            //         onSuccess();
            //     },
            //     response?.data?.data?.eventStreamCreated?.streamId
            // );
            if (response?.status !== 200) throw new Error(response?.data?.message);
            toast({ description: "Invoice escalated to master successfully", variant: "default" });
            onCloseModal();
            onSuccess();
        } catch (error: any) {
            toast({ description: error?.response?.data?.message, variant: "destructive" });
        } finally {
            setIsLoading(false);
            onCloseModal();
        }
    };
    const getRemarkHeaderDetails = async () => {
        try {
            const res = await getRemarkHeader()
            console.log(res)
            setRemarkHdr(res?.data?.data)
        } catch (error) {
            console.log(error)
        }
    }
    //M10-15136
    // Uses the existing Counter Code API to check what the counter master currently has on file.
    // MCI/DL: only presence matters — the values themselves are never compared to the invoice's.
    const getCounterMasterDetails = async () => {
        const counterCode = invoiceDetails?.counter_code;
        if (!counterCode) return;
        try {
            const res = await queryCounterDetailsById(`?counter_code=${counterCode}`);
            setCounterHasGstNumber(res?.data?.gst_available === "Yes");
            setCounterHasMciNumber(!!res?.data?.mci_register_number?.toString()?.trim());
            setCounterHasDlNumber(!!res?.data?.drug_license_number?.toString()?.trim());
        } catch (error) {
            console.log(error)
        }
    }
    //M10-15136
    // Reads the invoice's own MCI/DL numbers off the textract header. Only used to suppress the
    // matching "not present in master" remark when the invoice has no value to push either.
    // (GST is derived separately above, from invoiceDetails.counter_gst_number — no fetch needed.)
    const getInvoiceDetailsFromTextract = async () => {
        const attachmentId = invoiceDetails?.stockist_invoice_attachment_id; //M10-16244
        const fuid = invoiceDetails?.fuid_no;
        // Manually entered invoices have no textract header, so they never carry these numbers.
        // M10-16244: the textract API returns 400 without an attachment ID or FUID.
        if ((!attachmentId && !fuid) || !invoiceDetails?.is_textracted) {
            setInvoiceHasMciNumber(false);
            setInvoiceHasDlNumber(false);
            return;
        }
        try {
            const res = await getTextractData({ stockist_invoice_attachment_id: attachmentId, fuid }, invoiceDetails?.company_code); //M10-16244
            const textractHeaderData = res?.data?.data?.data?.header;
            setInvoiceHasMciNumber(!!textractHeaderData?.retailer_mci_number?.toString()?.trim());
            setInvoiceHasDlNumber(!!textractHeaderData?.retailer_dl_number?.toString()?.trim());
        } catch (error) {
            console.log(error)
            setInvoiceHasMciNumber(false);
            setInvoiceHasDlNumber(false);
        }
    }
    //M10-15136
    useEffect(() => {
        getRemarkHeaderDetails()
        getCounterMasterDetails() //M10-15136
        getInvoiceDetailsFromTextract() //M10-15136
    }, [invoiceDetails?.counter_code, invoiceDetails?.stockist_invoice_attachment_id, invoiceDetails?.fuid_no, invoiceDetails?.is_textracted]) //M10-15136, M10-16244

    return (
        <div className="flex flex-col gap-y-2 mb-4 w-[340px] max-h-[318px] mx-auto px-6 bg-white p-4 rounded-xl">
            <div className="flex justify-between items-center">
                <p className="text-lg font-semibold text-gray-800">Escalate to Master</p>
                <Button className="bg-transparent -mr-3" onClick={onCloseModal}>
                    <img src={crossIcon} alt="Close" className="w-5 h-5 invert cursor-pointer" />
                </Button>
            </div>

            <p className="text-sm text-gray-600 mt-4">
                Are you sure you want to escalate this invoice to master?
            </p>
            <div>
                <label htmlFor="" className="text-xs font-semibold text-gray-600">
                    Select a Remark Type
                </label>
                <Autocomplete
                    suggestionList={remarkOptions} //M10-15136
                    placeholder="Select a Remark Type"
                    displayKey="reason_desc"
                    uniqueKey="id"
                    onSelectionChange={(value: any) => {
                        console.log(value?.reason_desc)
                        setRemarkType(value?.id)
                    }}
                />
            </div>
            <label htmlFor="" className="text-xs font-semibold text-gray-600">
                Remarks
            </label>
            <Input
                placeholder="Enter remarks"
                value={remarks}
                onChange={(e: any) => setRemarks(e.target.value)}
            />
            <div className="flex justify-end gap-2 mt-4">
                <Button className="bg-gray-300 text-gray-700 hover:bg-[#BFC5CC] px-4 py-2 rounded-lg" onClick={onCloseModal}>
                    Cancel
                </Button>
                <Button
                    loaderSize="small"
                    isLoading={isLoading}
                    className="text-white bg-[#8051FF] hover:bg-[#6E3DFF] px-4 py-2 rounded-lg"
                    onClick={handleSaveChanges}
                >
                    Proceed
                </Button>
            </div>
        </div>
    );
};

export default EscalateToMaster;
